import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";
import { Download, Monitor, ShieldAlert, Package, RefreshCw, FolderOpen, MousePointer2 } from "lucide-react";
import { Button } from "../components/ui/button";

const fmtMB = (b) => (b / (1024 * 1024)).toFixed(1) + " MB";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function DesktopDownload() {
  const { isAdmin, loading, login } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/desktop-build/info"); setInfo(r.data); }
    catch { setInfo(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (isAdmin) load(); else setBusy(false); }, [isAdmin]);

  if (loading) return null;

  if (!isAdmin) {
    return (
      <div className="p-6 sm:p-10 max-w-2xl mx-auto" data-testid="desktop-download-denied">
        <div className="rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-8 text-center">
          <ShieldAlert className="h-10 w-10 text-cyan-400 mx-auto mb-3" />
          <h1 className="font-head font-black text-2xl uppercase text-white">Admin Only</h1>
          <p className="text-slate-400 text-sm mt-2">This page is restricted. Sign in with the admin account to download the desktop build.</p>
          <Button data-testid="desktop-download-login" onClick={login} className="mt-5 bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-950 font-bold">Admin Login</Button>
        </div>
      </div>
    );
  }

  const isZip = info?.kind === "portable_zip";
  const download = (arch) => { window.location.href = `${API}/desktop-build/download${arch ? `?arch=${arch}` : ""}`; };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="desktop-download-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-500/90 flex items-center gap-2"><Monitor className="h-4 w-4" /> Desktop · Admin</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Windows App</h1>
        <p className="text-slate-500 text-sm mt-1">Windows app (installer) that loads the live TRMD Performance site and runs the K-Line FTDI bridge embedded inside — same as the Android app. Requires internet + online login.</p>
      </div>

      {busy ? (
        <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-10 text-center text-slate-500 text-sm flex items-center justify-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Checking build…</div>
      ) : !info?.available ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-8 text-center" data-testid="desktop-download-unavailable">
          <Package className="h-9 w-9 text-amber-400 mx-auto mb-3" />
          <div className="text-white font-semibold">No build available yet</div>
          <p className="text-slate-400 text-sm mt-1">The desktop build hasn't been generated on the server.</p>
          <Button variant="ghost" onClick={load} className="mt-4 text-slate-300"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-6">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/30">
                <Monitor className="h-7 w-7 text-slate-950" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-head font-bold text-lg text-white truncate" data-testid="desktop-build-filename">{info.filename}</div>
                <div className="text-xs font-mono-tt text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                  <span data-testid="desktop-build-size">Size: {fmtMB(info.size)}</span>
                  <span>Built: {fmtDate(info.built_at)}</span>
                  <span className="text-emerald-400/90 uppercase">{isZip ? "Portable ZIP" : "Installer"}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button data-testid="desktop-download-btn" onClick={() => download("x64")} className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-bold">
                  <Download className="h-4 w-4 mr-2" /> Download 64-bit
                </Button>
                <Button data-testid="desktop-download-btn-ia32" onClick={() => download("ia32")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 font-semibold">
                  <Download className="h-4 w-4 mr-2" /> Download 32-bit (x86)
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-6">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">How to run on Windows</div>
            {isZip ? (
              <ol className="space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0">1</span><span>Click <b>Download</b> to get <span className="font-mono-tt text-emerald-400">{info.filename}</span>.</span></li>
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0">2</span><span className="flex items-center gap-1.5"><FolderOpen className="h-4 w-4 text-slate-400" /> Right-click the ZIP → <b>Extract All…</b> to a folder.</span></li>
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0">3</span><span className="flex items-center gap-1.5"><MousePointer2 className="h-4 w-4 text-slate-400" /> Open the folder and double-click <span className="font-mono-tt text-emerald-400">TRMD.exe</span>.</span></li>
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0">4</span><span>If Windows SmartScreen appears, click <b>More info → Run anyway</b> (app is unsigned).</span></li>
              </ol>
            ) : (
              <ol className="space-y-3 text-sm text-slate-300">
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0">1</span><span>Click <b>Download</b> to get the installer <span className="font-mono-tt text-emerald-400">{info.filename}</span>.</span></li>
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0">2</span><span>Double-click it and follow the setup wizard.</span></li>
                <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0">3</span><span>Launch <b>TRMD Performance</b> from the Desktop / Start Menu shortcut.</span></li>
              </ol>
            )}
            <div className="mt-4 text-xs text-slate-500 border-t border-slate-800 pt-3">Works fully offline · 64-bit Windows 10/11 · no account needed inside the app.</div>
          </div>
        </div>
      )}
    </div>
  );
}
